import { AnimatePresence, motion } from 'framer-motion';
import { Download, Edit, X } from 'lucide-react';
import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ForceCheckoutOverlay from '../components/CameraAttendance/ForceCheckoutOverlay';
import VisitorCheckInOverlay from '../components/CameraAttendance/VisitorCheckInOverlay';
import { getAllSites } from '../components/SiteManagement/siteApi';
import { productionTemplateApi } from '../components/Product/templateApi';
import Card from '../components/ui/card';
import { formatTimeDisplay, isOvernightShift, validateTimeRange } from '../utils/timeUtils';

const OverlayContext = createContext();

export const useOverlay = () => {
  const context = useContext(OverlayContext);
  if (!context) {
    throw new Error('useOverlay must be used within an OverlayProvider');
  }
  return context;
};

export const OverlayProvider = ({ children }) => {
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);
  const [showAssignEmployees, setShowAssignEmployees] = useState(false);
  const [showAssignEmployeesToZones, setShowAssignEmployeesToZones] = useState(false);
  const [showDeleteShiftWarning, setShowDeleteShiftWarning] = useState(false);
  const [showDeleteRideWarning, setShowDeleteRideWarning] = useState(false);
  const [showDeletePatrolWarning, setShowDeletePatrolWarning] = useState(false);
  const [showDeleteDishPricingRuleWarning, setShowDeleteDishPricingRuleWarning] = useState(false);
  const [showDeleteProductTemplateWarning, setShowDeleteProductTemplateWarning] = useState(false);
  const [showLeaveRequestDialog, setShowLeaveRequestDialog] = useState(false);
  const [showBillingPrint, setShowBillingPrint] = useState(false);
  const [showInvoicePrint, setShowInvoicePrint] = useState(false);
  const [showEstimationQuotationPrint, setShowEstimationQuotationPrint] = useState(false);
  const [showPurchaseOrderPrint, setShowPurchaseOrderPrint] = useState(false);
  const [showFdocIn, setShowFdocIn] = useState(false);
  const [showTaxPreview, setShowTaxPreview] = useState(false);
  const [showFdocInPreview, setShowFdocInPreview] = useState(false);
  const [showProductTemplateDetails, setShowProductTemplateDetails] = useState(false);
  const [showProductTemplatePdfPreview, setShowProductTemplatePdfPreview] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showVendorDetails, setShowVendorDetails] = useState(false);
  const [showWasteLoggingDetails, setShowWasteLoggingDetails] = useState(false);
  const [showFacilityBookingCalendarOverlay, setShowFacilityBookingCalendarOverlay] = useState(false);
  const [showSelectEmployeeForAlert, setShowSelectEmployeeForAlert] = useState(false);
  const [showProductionDetails, setShowProductionDetails] = useState(false);
  const [showInventoryDatePicker, setShowInventoryDatePicker] = useState(false);
  const [showLiveOrderDetails, setShowLiveOrderDetails] = useState(false);
  const [showForceCheckout, setShowForceCheckout] = useState(false);
  const [showVisitorCheckIn, setShowVisitorCheckIn] = useState(false);
  const [assignEmployeesType, setAssignEmployeesType] = useState('guards');
  
  // Select single employee for Service Recovery "Assign to Staff" (displayed above Alert Details)
  const [selectEmployeeForAlertData, setSelectEmployeeForAlertData] = useState({
    alertId: null,
    onSelect: null,
  });
  
  const [showBusinessAnalyticsChartOverlay, setShowBusinessAnalyticsChartOverlay] = useState(false);
  // const [assignEmployeesType, setAssignEmployeesType] = useState('guards');

  // Business Analytics chart overlay data (title + React node content)
  const [businessAnalyticsChartOverlayData, setBusinessAnalyticsChartOverlayData] = useState({
    title: '',
    content: null,
  });

  // Facility Booking Calendar overlay data
  const [facilityBookingCalendarOverlayData, setFacilityBookingCalendarOverlayData] = useState({
    events: [],
    selectedDate: null,
    currentView: null,
    onEventClick: null,
    onTitleClick: null,
    onDatesSet: null,
    eventContent: null,
  });
  
  // Additional data for zone management
  const [assignEmployeesToZonesData, setAssignEmployeesToZonesData] = useState({
    employeeType: 'guards',
    siteId: null,
    initialZoneGuards: {},
    shiftValidationData: null,
    onSave: null
  });
  
  // Delete shift warning data
  const [deleteShiftWarningData, setDeleteShiftWarningData] = useState({
    shiftIndex: null,
    onConfirm: null
  });
  
  // Delete ride warning data
  const [deleteRideWarningData, setDeleteRideWarningData] = useState({
    rideId: null,
    onConfirm: null
  });
  
  // Delete patrol warning data
  const [deletePatrolWarningData, setDeletePatrolWarningData] = useState({
    patrolId: null,
    onConfirm: null
  });

  // Delete dish pricing rule warning data
  const [deleteDishPricingRuleWarningData, setDeleteDishPricingRuleWarningData] = useState({
    rule: null,
    dishName: null,
    onConfirm: null
  });

  // Delete product template warning data
  const [deleteProductTemplateWarningData, setDeleteProductTemplateWarningData] = useState({
    templateName: '',
    templateId: null,
    onDeleted: null
  });
  
  // Leave request dialog data
  const [leaveRequestDialogData, setLeaveRequestDialogData] = useState({
    request: null,
    onApprove: null,
    onReject: null
  });
  
  // Billing print data
  const [billingPrintData, setBillingPrintData] = useState({
    bill: null,
    formatCurrency: null
  });

  // Invoice print data
  const [invoicePrintData, setInvoicePrintData] = useState({
    invoice: null,
    onClose: null
  });

  const [estimationQuotationPrintData, setEstimationQuotationPrintData] = useState({
    quotation: null
  });

  const [purchaseOrderPrintData, setPurchaseOrderPrintData] = useState({
    purchaseOrder: null
  });

  // FdocIn overlay data
  const [fdocInData, setFdocInData] = useState({
    onCollect: null
  });

// TaxPreview overlay data
const [taxPreviewData, setTaxPreviewData] = useState({
  documentUrl: null,
  documentNumber: null
});

// FdocInPreview overlay data
const [fdocInPreviewData, setFdocInPreviewData] = useState({
  documentData: null
});

// Product Template Details overlay data
const [productTemplateDetailsData, setProductTemplateDetailsData] = useState({
  template: null,
  onClose: null,
  onDeleted: null
});

// Product Template PDF Preview overlay data (props for PrintProductTemplatePdf)
const [productTemplatePdfPreviewData, setProductTemplatePdfPreviewData] = useState({});

// Customer Details overlay data
const [customerDetailsData, setCustomerDetailsData] = useState({
  customer: null,
  onDownload: null
});

// Vendor Details overlay data
const [vendorDetailsData, setVendorDetailsData] = useState({
  vendor: null,
  onDownload: null
});

// Waste Logging Details overlay data
  const [wasteLoggingDetailsData, setWasteLoggingDetailsData] = useState({
    date: null,
    groupedByDate: {},
    getDateTotals: null,
    navigate: null
  });

  // Production Details overlay data
  const [productionDetailsData, setProductionDetailsData] = useState({
    productionRun: null,
    onDownload: null
  });

  // Inventory Date Picker overlay data
  const [inventoryDatePickerData, setInventoryDatePickerData] = useState({
    mode: 'range',
    startDate: null,
    endDate: null,
    singleDate: null,
    onConfirm: null
  });

  // Live Order Details overlay data
  const [liveOrderDetailsData, setLiveOrderDetailsData] = useState({
    order: null
  });
  const [forceCheckoutData, setForceCheckoutData] = useState({
    title: 'Check Out',
    prompt: 'Do you want to force check out?',
    personName: '',
    onConfirm: null,
  });

  const [visitorCheckInData, setVisitorCheckInData] = useState({
    title: 'Visitor Check-in',
    initialValues: null,
    onSubmit: null,
  });

  
  // Overlay data states
  const [datePickerData, setDatePickerData] = useState({
    mode: 'range',
    variant: 'shift',
    startDate: new Date(),
    endDate: new Date(Date.now() + 6 * 24 * 60 * 60 * 1000),
    singleDate: new Date(),
    selectedDates: [],
    assignedDates: [],
  });
  const [startTimeData, setStartTimeData] = useState({ hours: 12, minutes: 0, period: 'AM' });
  const [endTimeData, setEndTimeData] = useState({ hours: 8, minutes: 0, period: 'AM' });
  const [assignedEmployees, setAssignedEmployees] = useState([]);
  const [assignedEmployeesToZones, setAssignedEmployeesToZones] = useState({});
  
  // Shift validation data
  const [shiftValidationData, setShiftValidationData] = useState({
    selectedSite: null,
    currentShift: null,
    date: null,
    startTime: null,
    endTime: null
  });

  // Memoized overnight shift detection
  const overnightShiftInfo = useMemo(() => {
    const isOvernight = isOvernightShift(startTimeData, endTimeData);
    const validation = validateTimeRange(startTimeData, endTimeData);
    
    return {
      isOvernight,
      isValid: validation.isValid,
      error: validation.error,
      startTimeFormatted: formatTimeDisplay(startTimeData),
      endTimeFormatted: formatTimeDisplay(endTimeData)
    };
  }, [startTimeData, endTimeData]);

  const openOverlay = (overlayType, data = {}) => {
    switch (overlayType) {
      case 'datePicker':
        if (data && Object.keys(data).length > 0) {
          setDatePickerData(prev => ({ ...prev, ...data }));
        }
        setShowDatePicker(true);
        break;
      case 'startTimePicker':
        setShowStartTimePicker(true);
        break;
      case 'endTimePicker':
        setShowEndTimePicker(true);
        break;
      case 'assignEmployees':
        setAssignEmployeesType(data.employeeType || 'guards');
        // Set shift validation data for employee availability checking
        if (data.shiftValidationData) {
          setShiftValidationData(data.shiftValidationData);
        }
        setShowAssignEmployees(true);
        break;
      case 'assignEmployeesToZones':
        setAssignEmployeesToZonesData({
          employeeType: data.employeeType || 'guards',
          siteId: data.siteId || null,
          initialZoneGuards: data.initialZoneGuards || {},
          shiftValidationData: data.shiftValidationData || null,
          onSave: data.onSave || null
        });
        setShowAssignEmployeesToZones(true);
        break;
      case 'deleteShiftWarning':
        setDeleteShiftWarningData({
          shiftIndex: data.shiftIndex || null,
          onConfirm: data.onConfirm || null
        });
        setShowDeleteShiftWarning(true);
        break;
      case 'deleteRideWarning':
        setDeleteRideWarningData({
          rideId: data.rideId || null,
          onConfirm: data.onConfirm || null
        });
        setShowDeleteRideWarning(true);
        break;
      case 'deletePatrolWarning':
        setDeletePatrolWarningData({
          patrolId: data.patrolId || null,
          onConfirm: data.onConfirm || null
        });
        setShowDeletePatrolWarning(true);
        break;
      case 'deleteDishPricingRuleWarning':
        setDeleteDishPricingRuleWarningData({
          rule: data.rule || null,
          dishName: data.dishName || null,
          onConfirm: data.onConfirm || null
        });
        setShowDeleteDishPricingRuleWarning(true);
        break;
      case 'deleteProductTemplateWarning':
        setDeleteProductTemplateWarningData({
          templateName: data.templateName || '',
          templateId: data.templateId || null,
          onDeleted: data.onDeleted || null
        });
        setShowDeleteProductTemplateWarning(true);
        break;
      case 'leaveRequestDialog':
        setLeaveRequestDialogData({
          request: data.request || null,
          onApprove: data.onApprove || null,
          onReject: data.onReject || null
        });
        setShowLeaveRequestDialog(true);
        break;
      case 'billingPrint':
        setBillingPrintData({
          bill: data.bill || null,
          formatCurrency: data.formatCurrency || null
        });
        setShowBillingPrint(true);
        break;
      case 'invoicePrint':
        setInvoicePrintData({
          invoice: data.invoice || null,
          onClose: data.onClose || null
        });
        setShowInvoicePrint(true);
        break;
      case 'estimationQuotationPrint':
        setEstimationQuotationPrintData({
          quotation: data.quotation || null
        });
        setShowEstimationQuotationPrint(true);
        break;
      case 'purchaseOrderPrint':
        setPurchaseOrderPrintData({
          purchaseOrder: data.purchaseOrder || null
        });
        setShowPurchaseOrderPrint(true);
        break;
      case 'fdocIn':
        setFdocInData({
          onCollect: data.onCollect || null
        });
        setShowFdocIn(true);
        break;
      case 'taxPreview':
        setTaxPreviewData({
          documentUrl: data.documentUrl || null,
          documentNumber: data.documentNumber || null
        });
        setShowTaxPreview(true);
        break;

      case 'fdocInPreview':
        setFdocInPreviewData({
          documentData: data.documentData || null
        });
        setShowFdocInPreview(true);
        break;

      case 'productTemplateDetails':
        setProductTemplateDetailsData({
          template: data.template || null,
          onClose: data.onClose || null,
          onDeleted: data.onDeleted || null
        });
        setShowProductTemplateDetails(true);
        break;
      case 'productTemplatePdfPreview':
        setProductTemplatePdfPreviewData(data || {});
        setShowProductTemplatePdfPreview(true);
        break;
      case 'customerDetails':
        setCustomerDetailsData({
          customer: data.customer || null,
          onDownload: data.onDownload || null
        });
        setShowCustomerDetails(true);
        break;
      case 'vendorDetails':
        setVendorDetailsData({
          vendor: data.vendor || null,
          onDownload: data.onDownload || null
        });
        setShowVendorDetails(true);
        break;
      case 'wasteLoggingDetails':
        setWasteLoggingDetailsData({
          date: data.date || null,
          groupedByDate: data.groupedByDate || {},
          getDateTotals: data.getDateTotals || null,
          navigate: data.navigate || null
        });
        setShowWasteLoggingDetails(true);
        break;
      case 'facilityBookingCalendar':
        setFacilityBookingCalendarOverlayData({
          events: data.events || [],
          selectedDate: data.selectedDate || null,
          currentView: data.currentView || 'timeGridWeek',
          onEventClick: data.onEventClick || null,
          onTitleClick: data.onTitleClick || null,
          onDatesSet: data.onDatesSet || null,
          eventContent: data.eventContent || null,
        });
        setShowFacilityBookingCalendarOverlay(true);
        break;
      case 'selectEmployeeForAlert':
        setSelectEmployeeForAlertData({
          alertId: data.alertId ?? null,
          onSelect: typeof data.onSelect === 'function' ? data.onSelect : null,
        });
        setShowSelectEmployeeForAlert(true);
        break;
      case 'productionDetails':
        setProductionDetailsData({
          productionRun: data.productionRun || null,
          onDownload: typeof data.onDownload === 'function' ? data.onDownload : null
        });
        setShowProductionDetails(true);
        break;
      case 'inventoryDatePicker':
        setInventoryDatePickerData({
          mode: data.mode || 'range',
          startDate: data.startDate || null,
          endDate: data.endDate || null,
          singleDate: data.singleDate || null,
          onConfirm: typeof data.onConfirm === 'function' ? data.onConfirm : null
        });
        setShowInventoryDatePicker(true);
        break;
      case 'businessAnalyticsChart':
        setBusinessAnalyticsChartOverlayData({
          title: data.title || 'Chart',
          content: data.content ?? null,
        });
        setShowBusinessAnalyticsChartOverlay(true);
        break;
      case 'liveOrderDetails':
        setLiveOrderDetailsData({
          order: data.order ?? null,
          onAccept: data.onAccept ?? null,
          onDecline: data.onDecline ?? null,
        });
        setShowLiveOrderDetails(true);
        break;
      case 'forceCheckout':
        setForceCheckoutData({
          title: data.title || 'Check Out',
          prompt: data.prompt || 'Do you want to force check out?',
          personName: data.personName || '',
          onConfirm: typeof data.onConfirm === 'function' ? data.onConfirm : null,
        });
        setShowForceCheckout(true);
        break;
      case 'visitorCheckIn':
        setVisitorCheckInData({
          title: data.title || 'Visitor Check-in',
          initialValues: data.initialValues || null,
          onSubmit: typeof data.onSubmit === 'function' ? data.onSubmit : null,
        });
        setShowVisitorCheckIn(true);
        break;
      default:
        break;
    }
  };

  const closeOverlay = (overlayType) => {
    switch (overlayType) {
      case 'datePicker':
        setShowDatePicker(false);
        break;
      case 'startTimePicker':
        setShowStartTimePicker(false);
        break;
      case 'endTimePicker':
        setShowEndTimePicker(false);
        break;
      case 'assignEmployees':
        setShowAssignEmployees(false);
        break;
      case 'assignEmployeesToZones':
        setShowAssignEmployeesToZones(false);
        break;
      case 'deleteShiftWarning':
        setShowDeleteShiftWarning(false);
        setDeleteShiftWarningData({ shiftIndex: null, onConfirm: null });
        break;
      case 'deleteRideWarning':
        setShowDeleteRideWarning(false);
        setDeleteRideWarningData({ rideId: null, onConfirm: null });
        break;
      case 'deletePatrolWarning':
        setShowDeletePatrolWarning(false);
        setDeletePatrolWarningData({ patrolId: null, onConfirm: null });
        break;
      case 'deleteDishPricingRuleWarning':
        setShowDeleteDishPricingRuleWarning(false);
        setDeleteDishPricingRuleWarningData({ rule: null, dishName: null, onConfirm: null });
        break;
      case 'deleteProductTemplateWarning':
        setShowDeleteProductTemplateWarning(false);
        setDeleteProductTemplateWarningData({ templateName: '', templateId: null, onDeleted: null });
        break;
      case 'leaveRequestDialog':
        setShowLeaveRequestDialog(false);
        setLeaveRequestDialogData({ request: null, onApprove: null, onReject: null });
        break;
      case 'billingPrint':
        setShowBillingPrint(false);
        setBillingPrintData({ bill: null, formatCurrency: null });
        break;
      case 'invoicePrint': {
        const onCloseCallback = invoicePrintData.onClose;
        setShowInvoicePrint(false);
        setInvoicePrintData({ invoice: null, onClose: null });
        if (typeof onCloseCallback === 'function') onCloseCallback();
        break;
      }
      case 'estimationQuotationPrint':
        setShowEstimationQuotationPrint(false);
        setEstimationQuotationPrintData({ quotation: null });
        break;
      case 'purchaseOrderPrint':
        setShowPurchaseOrderPrint(false);
        setPurchaseOrderPrintData({ purchaseOrder: null });
        break;
      case 'fdocIn':
        setShowFdocIn(false);
        setFdocInData({ onCollect: null });
        break;
      case 'taxPreview':
        setShowTaxPreview(false);
        setTaxPreviewData({ documentUrl: null, documentNumber: null });
        break;

      case 'fdocInPreview':
        setShowFdocInPreview(false);
        setFdocInPreviewData({ documentData: null });
        break;

      case 'productTemplateDetails':
        setShowProductTemplateDetails(false);
        setProductTemplateDetailsData({ template: null, onClose: null, onDeleted: null });
        break;
      case 'productTemplatePdfPreview':
        setShowProductTemplatePdfPreview(false);
        setProductTemplatePdfPreviewData({});
        break;
      case 'customerDetails':
        setShowCustomerDetails(false);
        setCustomerDetailsData({ customer: null, onDownload: null });
        break;
      case 'vendorDetails':
        setShowVendorDetails(false);
        setVendorDetailsData({ vendor: null, onDownload: null });
        break;
      case 'wasteLoggingDetails':
        setShowWasteLoggingDetails(false);
        setWasteLoggingDetailsData({ date: null, groupedByDate: {}, getDateTotals: null, navigate: null });
        break;
      case 'facilityBookingCalendar':
        setShowFacilityBookingCalendarOverlay(false);
        setFacilityBookingCalendarOverlayData({ events: [], selectedDate: null, currentView: null, onEventClick: null, onTitleClick: null, onDatesSet: null, eventContent: null });
        break;
      case 'selectEmployeeForAlert':
        setShowSelectEmployeeForAlert(false);
        setSelectEmployeeForAlertData({ alertId: null, onSelect: null });
        break;
      case 'productionDetails':
        setShowProductionDetails(false);
        setProductionDetailsData({ productionRun: null, onDownload: null });
        break;
      case 'inventoryDatePicker':
        setShowInventoryDatePicker(false);
        setInventoryDatePickerData({ mode: 'range', startDate: null, endDate: null, singleDate: null, onConfirm: null });
        break;
      case 'businessAnalyticsChart':
        setShowBusinessAnalyticsChartOverlay(false);
        setBusinessAnalyticsChartOverlayData({ title: '', content: null });
        break;
      case 'liveOrderDetails':
        setShowLiveOrderDetails(false);
        setLiveOrderDetailsData({ order: null, onAccept: null, onDecline: null });
        break;
      case 'forceCheckout':
        setShowForceCheckout(false);
        setForceCheckoutData({
          title: 'Check Out',
          prompt: 'Do you want to force check out?',
          personName: '',
          onConfirm: null,
        });
        break;
      case 'visitorCheckIn':
        setShowVisitorCheckIn(false);
        setVisitorCheckInData({
          title: 'Visitor Check-in',
          initialValues: null,
          onSubmit: null,
        });
        break;
      default:
        break;
    }
  };

  const value = {
    // Overlay visibility states
    showDatePicker,
    showStartTimePicker,
    showEndTimePicker,
    showAssignEmployees,
    showAssignEmployeesToZones,
    showDeleteShiftWarning,
    showDeleteRideWarning,
    showDeletePatrolWarning,
    showDeleteDishPricingRuleWarning,
    showDeleteProductTemplateWarning,
    showBillingPrint,
    showInvoicePrint,
    showEstimationQuotationPrint,
    showPurchaseOrderPrint,
    showFdocIn,
    showTaxPreview,
    showFdocInPreview,
    showProductTemplateDetails,
    showProductTemplatePdfPreview,
    showCustomerDetails,
    showVendorDetails,
    showWasteLoggingDetails,
    showFacilityBookingCalendarOverlay,
    showBusinessAnalyticsChartOverlay,
    showProductionDetails,
    showInventoryDatePicker,
    showLiveOrderDetails,
    showForceCheckout,
    showVisitorCheckIn,

    // Overlay data
    datePickerData,
    startTimeData,
    endTimeData,
    assignedEmployees,
    assignedEmployeesToZones,
    shiftValidationData,
    deleteShiftWarningData,
    deleteRideWarningData,
    deletePatrolWarningData,
    deleteDishPricingRuleWarningData,
    deleteProductTemplateWarningData,
    billingPrintData,
    invoicePrintData,
    estimationQuotationPrintData,
    purchaseOrderPrintData,
    fdocInData,
    taxPreviewData,
    fdocInPreviewData,
    productTemplateDetailsData,
    productTemplatePdfPreviewData,
    customerDetailsData,
    vendorDetailsData,
    wasteLoggingDetailsData,
    facilityBookingCalendarOverlayData,
    businessAnalyticsChartOverlayData,
    productionDetailsData,
    inventoryDatePickerData,
    liveOrderDetailsData,
    forceCheckoutData,
    visitorCheckInData,

    // Overnight shift detection
    overnightShiftInfo,
    
    // Functions
    openOverlay,
    closeOverlay,
    setDatePickerData,
    setStartTimeData,
    setEndTimeData,
    setAssignedEmployees,
    setAssignedEmployeesToZones,
    setShiftValidationData,
    setDeleteShiftWarningData,
    setDeleteRideWarningData,
    setDeletePatrolWarningData,
    setDeleteDishPricingRuleWarningData,
    setDeleteProductTemplateWarningData
  };

  return (
    <OverlayContext.Provider value={value}>
      {children}
      
      {/* Render overlays using Portal to ensure they appear at root level */}
      {showDatePicker && createPortal(
        <DatePickerOverlay
          isOpen={showDatePicker}
          onClose={() => closeOverlay('datePicker')}
          data={datePickerData}
          setData={setDatePickerData}
        />,
        document.body
      )}
      
      {showStartTimePicker && createPortal(
        <TimePickerOverlay
          isOpen={showStartTimePicker}
          onClose={() => closeOverlay('startTimePicker')}
          data={startTimeData}
          setData={setStartTimeData}
          title="Select Start Time"
          otherTime={endTimeData}
          isStartTime={true}
        />,
        document.body
      )}
      
      {showEndTimePicker && createPortal(
        <TimePickerOverlay
          isOpen={showEndTimePicker}
          onClose={() => closeOverlay('endTimePicker')}
          data={endTimeData}
          setData={setEndTimeData}
          title="Select End Time"
          otherTime={startTimeData}
          isStartTime={false}
        />,
        document.body
      )}
      
      {showAssignEmployees && createPortal(
        <AssignEmployeesOverlay
          isOpen={showAssignEmployees}
          onClose={() => closeOverlay('assignEmployees')}
          data={assignedEmployees}
          setData={setAssignedEmployees}
          employeeType={assignEmployeesType}
          shiftValidationData={shiftValidationData}
        />,
        document.body
      )}
      
      {showAssignEmployeesToZones && createPortal(
        <AssignEmployeesToZonesOverlay
          isOpen={showAssignEmployeesToZones}
          onClose={() => closeOverlay('assignEmployeesToZones')}
          data={assignedEmployeesToZones}
          setData={setAssignedEmployeesToZones}
          employeeType={assignEmployeesToZonesData.employeeType}
          siteId={assignEmployeesToZonesData.siteId}
          initialZoneGuards={assignEmployeesToZonesData.initialZoneGuards}
          shiftValidationData={assignEmployeesToZonesData.shiftValidationData}
          onSave={assignEmployeesToZonesData.onSave}
        />,
        document.body
      )}
      
      {/* Select single employee for Service Recovery "Assign to Staff" - z-index 10000 so it appears above Alert Details (9999) */}
      {showSelectEmployeeForAlert && createPortal(
        <SelectEmployeeForAlertOverlay
          isOpen={showSelectEmployeeForAlert}
          onClose={() => closeOverlay('selectEmployeeForAlert')}
          data={selectEmployeeForAlertData}
        />,
        document.body
      )}
      
      {showDeleteShiftWarning && createPortal(
        <DeleteShiftWarningOverlay
          isOpen={showDeleteShiftWarning}
          onClose={() => closeOverlay('deleteShiftWarning')}
          data={deleteShiftWarningData}
        />,
        document.body
      )}
      
      {showDeleteRideWarning && createPortal(
        <DeleteRideWarningOverlay
          isOpen={showDeleteRideWarning}
          onClose={() => closeOverlay('deleteRideWarning')}
          data={deleteRideWarningData}
        />,
        document.body
      )}
      
      {showDeletePatrolWarning && createPortal(
        <DeletePatrolWarningOverlay
          isOpen={showDeletePatrolWarning}
          onClose={() => closeOverlay('deletePatrolWarning')}
          data={deletePatrolWarningData}
        />,
        document.body
      )}
      
      {showDeleteDishPricingRuleWarning && createPortal(
        <DeleteDishPricingRuleWarningOverlay
          isOpen={showDeleteDishPricingRuleWarning}
          onClose={() => closeOverlay('deleteDishPricingRuleWarning')}
          data={deleteDishPricingRuleWarningData}
        />,
        document.body
      )}

      {showDeleteProductTemplateWarning && createPortal(
        <DeleteProductTemplateWarningOverlay
          isOpen={showDeleteProductTemplateWarning}
          onClose={() => closeOverlay('deleteProductTemplateWarning')}
          data={deleteProductTemplateWarningData}
        />,
        document.body
      )}
      
      {showLeaveRequestDialog && createPortal(
        <LeaveRequestDialogOverlay
          isOpen={showLeaveRequestDialog}
          onClose={() => closeOverlay('leaveRequestDialog')}
          data={leaveRequestDialogData}
        />,
        document.body
      )}
      
      {showBillingPrint && createPortal(
        <BillingPrintOverlay
          isOpen={showBillingPrint}
          onClose={() => closeOverlay('billingPrint')}
          data={billingPrintData}
        />,
        document.body
      )}

      {showInvoicePrint && createPortal(
        <InvoicePrintOverlay
          isOpen={showInvoicePrint}
          onClose={() => closeOverlay('invoicePrint')}
          data={invoicePrintData}
        />,
        document.body
      )}

      {showEstimationQuotationPrint && createPortal(
        <EstimationQuotationPrintOverlay
          isOpen={showEstimationQuotationPrint}
          onClose={() => closeOverlay('estimationQuotationPrint')}
          data={estimationQuotationPrintData}
        />,
        document.body
      )}

      {showPurchaseOrderPrint && createPortal(
        <PurchaseOrderPrintOverlay
          isOpen={showPurchaseOrderPrint}
          onClose={() => closeOverlay('purchaseOrderPrint')}
          data={purchaseOrderPrintData}
        />,
        document.body
      )}

      {showFdocIn && createPortal(
        <FdocInOverlay
          isOpen={showFdocIn}
          onClose={() => closeOverlay('fdocIn')}
          onCollect={fdocInData.onCollect}
          openOverlay={openOverlay}
          closeOverlay={closeOverlay}
        />,
        document.body
      )}

      {showTaxPreview && createPortal(
        <TaxPreviewOverlay
          isOpen={showTaxPreview}
          onClose={() => closeOverlay('taxPreview')}
          documentUrl={taxPreviewData.documentUrl}
          documentNumber={taxPreviewData.documentNumber}
        />,
        document.body
      )}

      {showFdocInPreview && createPortal(
        <FdocInPreviewOverlay
          isOpen={showFdocInPreview}
          onClose={() => closeOverlay('fdocInPreview')}
          documentData={fdocInPreviewData.documentData}
        />,
        document.body
      )}

      {showProductTemplateDetails && createPortal(
        <ProductTemplateDetailsOverlay
          isOpen={showProductTemplateDetails}
          onClose={() => closeOverlay('productTemplateDetails')}
          data={productTemplateDetailsData}
        />,
        document.body
      )}

      {showProductTemplatePdfPreview && createPortal(
        <ProductTemplatePdfPreviewOverlay
          isOpen={showProductTemplatePdfPreview}
          onClose={() => closeOverlay('productTemplatePdfPreview')}
          data={productTemplatePdfPreviewData}
        />,
        document.body
      )}

      {showCustomerDetails && createPortal(
        <CustomerDetailsOverlay
          isOpen={showCustomerDetails}
          onClose={() => closeOverlay('customerDetails')}
          data={customerDetailsData}
        />,
        document.body
      )}

      {showVendorDetails && createPortal(
        <VendorDetailsOverlay
          isOpen={showVendorDetails}
          onClose={() => closeOverlay('vendorDetails')}
          data={vendorDetailsData}
        />,
        document.body
      )}

      {showWasteLoggingDetails && createPortal(
        <WasteLoggingDetailsOverlay
          isOpen={showWasteLoggingDetails}
          onClose={() => closeOverlay('wasteLoggingDetails')}
          data={wasteLoggingDetailsData}
        />,
        document.body
      )}

      {showFacilityBookingCalendarOverlay && createPortal(
        <FacilityBookingCalendarOverlay
          isOpen={showFacilityBookingCalendarOverlay}
          onClose={() => closeOverlay('facilityBookingCalendar')}
          data={facilityBookingCalendarOverlayData}
        />,
        document.body
      )}

      {showBusinessAnalyticsChartOverlay && createPortal(
        <BusinessAnalyticsChartOverlay
          isOpen={showBusinessAnalyticsChartOverlay}
          onClose={() => closeOverlay('businessAnalyticsChart')}
          title={businessAnalyticsChartOverlayData.title}
          content={businessAnalyticsChartOverlayData.content}
        />,
        document.body
      )}

      {showProductionDetails && createPortal(
        <ProductionDetailsOverlay
          isOpen={showProductionDetails}
          onClose={() => closeOverlay('productionDetails')}
          data={productionDetailsData}
        />,
        document.body
      )}

      {showInventoryDatePicker && createPortal(
        <InventoryDatePickerOverlay
          isOpen={showInventoryDatePicker}
          onClose={() => closeOverlay('inventoryDatePicker')}
          data={inventoryDatePickerData}
        />,
        document.body
      )}

      {showLiveOrderDetails && createPortal(
        <LiveOrderDetailsOverlayWrapper
          onClose={() => closeOverlay('liveOrderDetails')}
          order={liveOrderDetailsData.order}
          onAccept={liveOrderDetailsData.order && liveOrderDetailsData.onAccept
            ? (order) => {
                liveOrderDetailsData.onAccept(order);
                setLiveOrderDetailsData((prev) => ({ ...prev, order: { ...order, status: 'Accepted' } }));
              }
            : undefined}
          onDecline={liveOrderDetailsData.order && liveOrderDetailsData.onDecline
            ? (order) => {
                liveOrderDetailsData.onDecline(order);
                closeOverlay('liveOrderDetails');
              }
            : undefined}
        />,
        document.body
      )}

      {showForceCheckout && createPortal(
        <ForceCheckoutOverlay
          isOpen={showForceCheckout}
          onClose={() => closeOverlay('forceCheckout')}
          data={forceCheckoutData}
        />,
        document.body
      )}

      {showVisitorCheckIn && createPortal(
        <VisitorCheckInOverlay
          isOpen={showVisitorCheckIn}
          onClose={() => closeOverlay('visitorCheckIn')}
          data={visitorCheckInData}
        />,
        document.body
      )}
    </OverlayContext.Provider>
  );
};

// Overlay components that will be rendered at root level
const DatePickerOverlay = ({ isOpen, onClose, data, setData }) => {
  if (!isOpen) return null;

  if (data.variant === 'cleaning') {
    const CleaningScheduleDatePicker = require('../components/CleaningSchedule/CleaningScheduleDatePicker').default;
    return (
      <CleaningScheduleDatePicker
        isOpen={isOpen}
        onClose={onClose}
        title="Select dates for community shift"
        subtitle="Pick date or select multiple dates"
        initialSelectedDates={data.selectedDates || []}
        assignedDates={data.assignedDates || []}
        onConfirm={result => {
          setData({
            ...data,
            mode: result.mode,
            selectedDates: result.selectedDates,
            startDate: result.startDate,
            endDate: result.endDate,
            singleDate: result.singleDate,
          });
          onClose();
        }}
      />
    );
  }

  // Import DatePicker component dynamically to avoid circular dependencies
  const DatePicker = require('../components/ShiftManagement/datepicker').default;

  return (
    <DatePicker
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={(result) => {
        setData({
          ...data,
          mode: result.mode,
          startDate: result.startDate || data.startDate,
          endDate: result.endDate || data.endDate,
          singleDate: result.singleDate || data.singleDate,
        });
        onClose();
      }}
      initialMode={data.mode}
      initialStartDate={data.startDate}
      initialEndDate={data.endDate}
      initialSingleDate={data.singleDate}
      title="Select Date for Shift"
    />
  );
};

const TimePickerOverlay = ({ isOpen, onClose, data, setData, title, otherTime, isStartTime }) => {
  if (!isOpen) return null;
  
  // Import TimePicker component dynamically to avoid circular dependencies
  const TimePicker = require('../components/ShiftManagement/TimePicker').default;
  
  return (
    <TimePicker
      isOpen={isOpen}
      onClose={onClose}
      onTimeSelect={(time) => {
        setData(time);
        onClose();
      }}
      initialTime={data}
      title={title}
      otherTime={otherTime}
      isStartTime={isStartTime}
    />
  );
};

const AssignEmployeesOverlay = ({ isOpen, onClose, data, setData, employeeType, shiftValidationData }) => {
  if (!isOpen) return null;
  
  // Import AssignEmployees component dynamically to avoid circular dependencies
  const AssignEmployees = require('../components/ShiftManagement/AssignEmployees').default;
  
  return (
    <AssignEmployees
      isOpen={isOpen}
      onClose={onClose}
      onSave={(employees) => {
        console.log('🔍 OverlayContext - Setting assigned employees:', employees);
        setData(employees);
        onClose();
      }}
      initialSelectedEmployees={data}
      employeeType={employeeType}
      shiftValidationData={shiftValidationData}
    />
  );
};

const AssignEmployeesToZonesOverlay = ({ isOpen, onClose, data, setData, employeeType, siteId, initialZoneGuards, shiftValidationData, onSave }) => {
  if (!isOpen) return null;
  
  // Import AssignEmployeesToZones component dynamically to avoid circular dependencies
  const AssignEmployeesToZones = require('../components/ShiftManagement/AssignEmployeesToZones').default;
  
  return (
    <AssignEmployeesToZones
      isOpen={isOpen}
      onClose={onClose}
      onSave={(zoneGuards) => {
        setData(zoneGuards);
        if (onSave) {
          onSave(zoneGuards);
        }
        onClose();
      }}
      initialSelectedEmployees={data}
      employeeType={employeeType}
      siteId={siteId}
      initialZoneGuards={initialZoneGuards}
      shiftValidationData={shiftValidationData}
    />
  );
};

const SelectEmployeeForAlertOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  const SelectSingleEmployee = require('../components/ServiceRecovery/SelectSingleEmployee').default;
  return (
    <SelectSingleEmployee
      isOpen={isOpen}
      onClose={onClose}
      onSelect={(employee) => {
        if (data && typeof data.onSelect === 'function') {
          data.onSelect(employee);
        }
        onClose();
      }}
      title="Assign to Staff"
    />
  );
};

const LeaveRequestDialogOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import LeaveRequestDialog component dynamically to avoid circular dependencies
  const LeaveRequestDialog = require('../components/LeaveManagement/LeaveRequestDialog').default;
  
  return (
    <LeaveRequestDialog
      request={data.request}
      isOpen={isOpen}
      onClose={onClose}
      onApprove={(request) => {
        if (data.onApprove) {
          data.onApprove(request);
        }
        onClose();
      }}
      onReject={(request) => {
        if (data.onReject) {
          data.onReject(request);
        }
        onClose();
      }}
    />
  );
};

const DeleteShiftWarningOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import DeleteShiftWarning component dynamically to avoid circular dependencies
  const DeleteShiftWarning = require('../components/ShiftManagement/delete-shift-warning').default;
  
  // We need to access the parent component's shift management functions
  // This will be handled by passing the shift management functions through the context
  // For now, we'll create a simple implementation that can be enhanced later
  
  const handleConfirm = () => {
    // Call the parent component's shift deletion function if provided
    if (data.onConfirm && typeof data.onConfirm === 'function') {
      data.onConfirm(data.shiftIndex);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <DeleteShiftWarning
      isVisible={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );
};

const DeleteRideWarningOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import DeleteRideWarning component dynamically to avoid circular dependencies
  const DeleteRideWarning = require('../components/ScheduleARide/delete-ride-warning').default;
  
  const handleConfirm = () => {
    // Call the parent component's ride deletion function if provided
    if (data.onConfirm && typeof data.onConfirm === 'function') {
      data.onConfirm(data.rideId);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <DeleteRideWarning
      isVisible={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );
};

const DeletePatrolWarningOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import DeletePatrolWarning component dynamically to avoid circular dependencies
  const DeletePatrolWarning = require('../components/Patrolling/delete-patrol-warning').default;
  
  const handleConfirm = () => {
    // Call the parent component's patrol deletion function if provided
    if (data.onConfirm && typeof data.onConfirm === 'function') {
      data.onConfirm(data.patrolId);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <DeletePatrolWarning
      isVisible={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
    />
  );
};

const DeleteDishPricingRuleWarningOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import DeleteDishPricingRuleWarning component dynamically to avoid circular dependencies
  const DeleteDishPricingRuleWarning = require('../components/DynamicPricing/delete-dish-pricing-rule-warning').default;
  
  const handleConfirm = () => {
    // Call the parent component's deletion function if provided
    if (data.onConfirm && typeof data.onConfirm === 'function') {
      data.onConfirm(data.rule);
    }
    onClose();
  };
  
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <DeleteDishPricingRuleWarning
      isVisible={isOpen}
      onCancel={handleCancel}
      onConfirm={handleConfirm}
      dishName={data.dishName || ''}
    />
  );
};

const DeleteProductTemplateWarningOverlay = ({ isOpen, onClose, data }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    if (isSubmitting) return;
    if (!data.templateId) {
      window.alert('Template ID is missing. Unable to delete template.');
      return;
    }

    try {
      setIsSubmitting(true);

      const result = await productionTemplateApi.archiveTemplate(data.templateId);
      if (!result?.success) {
        throw new Error(result?.message || 'Failed to delete template');
      }

      if (data.onDeleted && typeof data.onDeleted === 'function') {
        data.onDeleted(result);
      }
      onClose();
    } catch (error) {
      console.error('DeleteProductTemplateWarningOverlay: Error deleting template:', error);
      window.alert(error?.message || 'Failed to delete template');
    } finally {
      setIsSubmitting(false);
    }
  };

  const templateName = data.templateName || 'this';

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-3">Confirm Delete</h3>
            <p className="text-sm text-gray-700 mb-6">
              Are you sure? do you want to delete the '{templateName}' Product Template
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="flex-1 rounded-xl bg-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={isSubmitting}
                className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? 'Deleting...' : 'Yes, delete Template'}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const BillingPrintOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Debug logging
  console.log('BillingPrintOverlay - isOpen:', isOpen);
  console.log('BillingPrintOverlay - data:', data);
  console.log('BillingPrintOverlay - data.bill:', data?.bill);
  console.log('BillingPrintOverlay - data.formatCurrency:', data?.formatCurrency);
  
  // Import BillingPrint component dynamically to avoid circular dependencies
  const BillingPrint = require('../components/BillCustomer/Billing_print').default;
  
  // Ensure we have valid data
  if (!data || !data.bill) {
    console.error('BillingPrintOverlay: Missing bill data!', { data });
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="rounded-2xl bg-white p-6 text-center">
          <p className="text-lg font-semibold text-red-600">Error: Bill data is missing</p>
          <p className="mt-2 text-sm text-gray-600">Please try generating the bill again.</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-full bg-red-500 px-6 py-2 text-sm font-semibold text-white hover:bg-red-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <BillingPrint
      bill={data.bill}
      isOpen={isOpen}
      onClose={onClose}
      formatCurrency={data.formatCurrency}
    />
  );
};

const InvoicePrintOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  // Import InvoicePrint component dynamically to avoid circular dependencies
  const InvoicePrint = require('../components/Invoice/Invoice_print').default;

  return (
    <InvoicePrint
      invoice={data.invoice}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

const EstimationQuotationPrintOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  const EstimationQuotationPrint = require('../components/EstimationQuotation/Estimation_Quotation_Print').default;

  return (
    <EstimationQuotationPrint
      quotation={data.quotation}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

const PurchaseOrderPrintOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  const PurchaseOrderPrint = require('../components/PurchaseOrder/PurchaseOrderPrint').default;

  return (
    <PurchaseOrderPrint
      purchaseOrder={data.purchaseOrder}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

const FdocInOverlay = ({ isOpen, onClose, onCollect, openOverlay, closeOverlay }) => {
  if (!isOpen) return null;

  // Import FdocIn component dynamically to avoid circular dependencies
  const FdocIn = require('../components/Tax/FdocIn').default;

  return (
    <FdocIn
      isOpen={isOpen}
      onClose={onClose}
      onCollect={(data) => {
        if (onCollect && typeof onCollect === 'function') {
          onCollect(data);
        }
        onClose();
      }}
      openOverlay={openOverlay}
      closeOverlay={closeOverlay}
    />
  );
};

const TaxPreviewOverlay = ({ isOpen, onClose, documentUrl, documentNumber }) => {
  if (!isOpen) return null;

  // Import TaxPreview component dynamically to avoid circular dependencies
  const TaxPreview = require('../components/Tax/TaxPreview').default;

  return (
    <TaxPreview
      isOpen={isOpen}
      onClose={onClose}
      documentUrl={documentUrl}
      documentNumber={documentNumber}
    />
  );
};

const BusinessAnalyticsChartOverlay = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          className="bg-white rounded-xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
          style={{ maxHeight: '90vh' }}
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-auto p-6 min-h-0" style={{ minHeight: '400px' }}>
            {content}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const FdocInPreviewOverlay = ({ isOpen, onClose, documentData }) => {
  if (!isOpen) return null;

  // Import FdocInPreview component dynamically to avoid circular dependencies
  const FdocInPreview = require('../components/Tax/FdocInPreview').default;

  return (
    <FdocInPreview
      isOpen={isOpen}
      onClose={onClose}
      documentData={documentData}
    />
  );
};

const ProductTemplateDetailsOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data.template) return null;

  // Import ProductTemplateDetailsModal component dynamically to avoid circular dependencies
  const ProductTemplateDetailsModal = require('../components/Product/ProductTemplateDetailsModal').default;

  return (
    <ProductTemplateDetailsModal
      template={data.template}
      isOpen={isOpen}
      onDeleteSuccess={data.onDeleted}
      onClose={() => {
        if (data.onClose && typeof data.onClose === 'function') {
          data.onClose();
        }
        onClose();
      }}
    />
  );
};

const ProductTemplatePdfPreviewOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;

  const PrintProductTemplatePdf = require('../components/Product/Print_Product_template_pdf').default;

  return (
    <>
      <style>{`
        @media print {
          @page {
            size: JIS-B4;
            margin: 12mm;
          }
          /* Force backgrounds and colors to print (same as on-screen) */
          .product-template-pdf-overlay,
          .product-template-pdf-overlay * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          /* Section headers: green (match bg-green-600) */
          .product-template-pdf-overlay .product-template-pdf-section-header {
            background-color: #16a34a !important;
            color: #ffffff !important;
          }
          /* SOP sub-headers: orange (match bg-orange-500) */
          .product-template-pdf-overlay .product-template-pdf-sop-subheader {
            background-color: #f97316 !important;
            color: #ffffff !important;
          }
          /* BOM table header row */
          .product-template-pdf-overlay .product-template-pdf-bom-thead {
            background-color: #f3f4f6 !important;
          }
          .product-template-pdf-overlay .product-template-pdf-bom-thead th {
            color: #374151 !important;
            border-color: #d1d5db !important;
          }
          body * { visibility: hidden; }
          .product-template-pdf-overlay,
          .product-template-pdf-overlay * { visibility: visible; }
          .product-template-pdf-overlay {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            right: 0 !important;
            width: 100% !important;
            min-height: 100% !important;
            background: white !important;
            z-index: 99999 !important;
            padding: 0 !important;
            display: block !important;
            align-items: stretch !important;
            justify-content: flex-start !important;
          }
          .product-template-pdf-overlay .product-template-pdf-overlay-panel {
            max-height: none !important;
            height: auto !important;
            box-shadow: none !important;
            display: block !important;
          }
          .product-template-pdf-overlay .product-template-pdf-overlay-header {
            display: none !important;
          }
          .product-template-pdf-overlay .product-template-pdf-overlay-body {
            overflow: visible !important;
            padding: 0 !important;
            height: auto !important;
            max-height: none !important;
            display: block !important;
          }
        }
      `}</style>
      <div
        className="product-template-pdf-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="product-template-pdf-overlay-panel relative flex max-h-[90vh] w-full max-w-4xl flex-col rounded-xl bg-white shadow-xl">
          <div className="product-template-pdf-overlay-header flex shrink-0 items-center justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <h3 className="text-lg font-semibold text-gray-800">
              Product Template Preview
            </h3>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.print()}
                className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                aria-label="Print / Save as PDF"
              >
                <Download className="w-4 h-4" style={{ color: 'white' }} />
                <span style={{ color: 'white' }}>Print / Save as PDF</span>
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-200 hover:text-gray-700"
                aria-label="Close preview"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="product-template-pdf-overlay-body flex-1 overflow-y-auto p-4">
            <PrintProductTemplatePdf {...data} />
          </div>
        </div>
      </div>
    </>
  );
};

const CustomerDetailsOverlay = ({ isOpen, onClose, data }) => {
  const [sitesMap, setSitesMap] = useState({});
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  // Fetch all sites when overlay opens
  useEffect(() => {
    if (!isOpen || !data.customer) return;

    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesResponse = await getAllSites();
        
        // Handle different response formats
        let sitesArray = [];
        if (Array.isArray(sitesResponse)) {
          sitesArray = sitesResponse;
        } else if (sitesResponse.sites && Array.isArray(sitesResponse.sites)) {
          sitesArray = sitesResponse.sites;
        } else if (sitesResponse.data && Array.isArray(sitesResponse.data)) {
          sitesArray = sitesResponse.data;
        }

        // Create a map of site_id to site_name
        const map = {};
        sitesArray.forEach(site => {
          const siteId = site.site_id || site.id;
          const siteName = site.site_name || site.name || siteId;
          if (siteId) {
            map[siteId] = siteName;
          }
        });
        setSitesMap(map);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesMap({});
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
  }, [isOpen, data.customer]);

  if (!isOpen || !data.customer) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const handleDownload = () => {
    if (data.onDownload && typeof data.onDownload === 'function') {
      data.onDownload(data.customer);
    }
  };

  const customer = data.customer;

  // Helper function to get site name from site ID or object
  const getSiteName = (site) => {
    if (typeof site === 'string') {
      // If it's a string (site ID), look it up in the map
      return sitesMap[site] || site;
    } else if (site && typeof site === 'object') {
      // If it's an object, try to get site_name or name
      return site.site_name || site.name || site.site_id || site.id || 'Unknown Site';
    }
    return 'Unknown Site';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-800">
                {customer.customerName} - Details
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Customer Name</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.customerName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Email</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.email || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contact Person</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.contactPerson || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contact Mobile</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.contactMobile || customer.contactPhone || 'N/A'}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-semibold text-gray-600">Billing Address</label>
                  <p className="text-base text-gray-800 mt-1">
                    {customer.address 
                      ? `${customer.address}, ${customer.city}, ${customer.state} - ${customer.pinCode}, ${customer.country}`
                      : 'N/A'}
                  </p>
                </div>
                {customer.fullCustomer?.shipping_address && (
                  <div className="col-span-2">
                    <label className="text-sm font-semibold text-gray-600">Shipping Address</label>
                    <p className="text-base text-gray-800 mt-1">
                      {customer.fullCustomer.shipping_address.street 
                        ? `${customer.fullCustomer.shipping_address.street}, ${customer.fullCustomer.shipping_address.city}, ${customer.fullCustomer.shipping_address.state} - ${customer.fullCustomer.shipping_address.pincode}, ${customer.fullCustomer.shipping_address.country || 'India'}`
                        : 'Same as billing address'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-semibold text-gray-600">PAN Number</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.panNumber || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">GST Number</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.gstNumber || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Payment Terms</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.paymentTerms || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Status</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    <span className={`px-2 py-1 rounded text-sm ${
                      customer.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {customer.status || 'active'}
                    </span>
                  </p>
                </div>
                {customer.bankName && (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Bank Name</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {customer.bankName}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Account Number</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {customer.accountNo || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">IFSC Code</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {customer.ifscNo || 'N/A'}
                      </p>
                    </div>
                  </>
                )}
                {customer.fullCustomer?.primary_contact && (
                  <div className="col-span-2">
                    <label className="text-sm font-semibold text-gray-600">Primary Contact</label>
                    <p className="text-base text-gray-800 mt-1">
                      {customer.fullCustomer.primary_contact.name || 'N/A'}
                      {customer.fullCustomer.primary_contact.role && ` (${customer.fullCustomer.primary_contact.role})`}
                      {customer.fullCustomer.primary_contact.email && ` - ${customer.fullCustomer.primary_contact.email}`}
                      {customer.fullCustomer.primary_contact.mobile && ` - ${customer.fullCustomer.primary_contact.mobile}`}
                    </p>
                  </div>
                )}
                {customer.fullCustomer?.secondary_contact && (
                  <div className="col-span-2">
                    <label className="text-sm font-semibold text-gray-600">Secondary Contact</label>
                    <p className="text-base text-gray-800 mt-1">
                      {customer.fullCustomer.secondary_contact.name || 'N/A'}
                      {customer.fullCustomer.secondary_contact.role && ` (${customer.fullCustomer.secondary_contact.role})`}
                      {customer.fullCustomer.secondary_contact.email && ` - ${customer.fullCustomer.secondary_contact.email}`}
                      {customer.fullCustomer.secondary_contact.mobile && ` - ${customer.fullCustomer.secondary_contact.mobile}`}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-semibold text-gray-600">Created Date</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {customer.createdAt 
                      ? new Date(customer.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Associated Sites</h3>
                <div className="space-y-2">
                  {isLoadingSites ? (
                    <p className="text-sm text-gray-500">Loading sites...</p>
                  ) : Array.isArray(customer.sites) && customer.sites.length > 0 ? (
                    customer.sites.map((site, index) => {
                      const siteName = getSiteName(site);
                      return (
                        <div key={index} className="px-4 py-2 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-800">
                            {siteName}
                          </p>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-sm text-gray-500">No sites associated</p>
                  )}
                </div>
              </div>

              {/* <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleDownload}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-700 px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <Download className="w-5 h-5 text-white stroke-white" strokeWidth={2} />
                  <span style={{ color: '#ffffff' }}>Download Details</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div> */}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const WasteLoggingDetailsOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data.date) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const logsForDate = data.groupedByDate[data.date] || [];
  const totals = data.getDateTotals ? data.getDateTotals(data.date) : { totalItems: 0, totalLoss: 0 };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="bg-white rounded-3xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-3xl">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">
                  Waste Logs for {new Date(data.date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {totals.totalItems} item(s) • Total Loss: ₹{totals.totalLoss.toFixed(2)}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors p-2"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {logsForDate.length > 0 ? (
                <div className="space-y-4">
                  {logsForDate.map((log, index) => (
                    <motion.div
                      key={log.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border border-gray-200 rounded-2xl p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {log.item || 'Unknown Item'}
                              </h3>
                              <p className="text-sm text-gray-500 mt-1">
                                {log.siteName || 'Unknown Site'}
                              </p>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              log.category === 'Production waste' 
                                ? 'bg-blue-100 text-blue-700' 
                                : log.category === 'Service waste'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-orange-100 text-orange-700'
                            }`}>
                              {log.category}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Quantity:</span>
                              <p className="font-medium text-gray-900">{log.quantity}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Cost per Unit:</span>
                              <p className="font-medium text-gray-900">₹{log.itemCost.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Total Loss:</span>
                              <p className="font-medium text-red-600">₹{log.estimatedLoss.toFixed(2)}</p>
                            </div>
                            <div>
                              <span className="text-gray-500">Type:</span>
                              <p className="font-medium text-gray-900">
                                {log.isStockItem ? 'Stock Item' : 'Non-Stock Item'}
                              </p>
                            </div>
                          </div>

                          {log.reason && (
                            <div>
                              <span className="text-gray-500 text-sm">Reason:</span>
                              <p className="text-gray-700 mt-1">{log.reason}</p>
                            </div>
                          )}

                          {log.notes && (
                            <div>
                              <span className="text-gray-500 text-sm">Notes:</span>
                              <p className="text-gray-700 mt-1">{log.notes}</p>
                            </div>
                          )}

                          {log.imageKey && (
                            <div>
                              <span className="text-gray-500 text-sm">Image:</span>
                              <p className="text-gray-700 mt-1 text-xs">Image available</p>
                            </div>
                          )}

                          {log.createdAt && (
                            <div className="text-xs text-gray-400">
                              Created: {new Date(log.createdAt).toLocaleString()}
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => {
                              if (data.navigate && typeof data.navigate === 'function') {
                                data.navigate('/onboarding-waste-logging/create', {
                                  state: {
                                    editMode: true,
                                    wasteLogId: log.id,
                                    logDate: log.logDate,
                                    siteId: log.siteId,
                                    siteName: log.siteName,
                                    isStockItem: log.isStockItem,
                                    stockItemId: log.stockItemId,
                                    productId: log.productId,
                                    productName: log.item,
                                    quantity: log.quantity,
                                    itemCost: log.itemCost,
                                    category: log.category,
                                    reason: log.reason,
                                    notes: log.notes,
                                    estimatedLoss: log.estimatedLoss,
                                    imageKey: log.imageKey,
                                  }
                                });
                              }
                              onClose();
                            }}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-gray-500">No waste logs found for this date.</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Helpers for overlay date picker (same behaviour as FacilityBooking inline picker)
const getWeekNumber = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
};
const getDateFromWeek = (weekNumber, year) => {
  const simple = new Date(year, 0, 1 + (weekNumber - 1) * 7);
  const dow = simple.getDay();
  if (dow <= 4) {
    simple.setDate(simple.getDate() - simple.getDay() + 1);
  } else {
    simple.setDate(simple.getDate() + 8 - simple.getDay());
  }
  return simple;
};
const getWeeksForYear = (year) => {
  const weeks = [];
  const maxWeeks = getWeekNumber(new Date(year, 11, 31)) === 1 ? 52 : 53;
  for (let i = 1; i <= maxWeeks; i++) {
    const weekStart = getDateFromWeek(i, year);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    weeks.push({
      number: i,
      start: weekStart,
      end: weekEnd,
      label: `Week ${i} (${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
    });
  }
  return weeks;
};
const getDaysForMonth = (month, year) => {
  const days = [];
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i);
    days.push({
      day: i,
      date,
      label: `${i} ${date.toLocaleDateString('en-US', { weekday: 'short' })}`
    });
  }
  return days;
};

// Facility Booking Calendar Full-Screen Overlay Component
const FacilityBookingCalendarOverlay = ({ isOpen, onClose, data }) => {
  const overlayCalendarRef = useRef(null);
  const overlayPickerRef = useRef(null);
  const [overlaySelectedDate, setOverlaySelectedDate] = useState(() => data?.selectedDate || new Date());
  const [overlayCurrentView, setOverlayCurrentView] = useState(() => data?.currentView || 'timeGridWeek');
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(() => (data?.selectedDate ? new Date(data.selectedDate).getMonth() : new Date().getMonth()));
  const [pickerYear, setPickerYear] = useState(() => (data?.selectedDate ? new Date(data.selectedDate).getFullYear() : new Date().getFullYear()));
  const [pickerWeek, setPickerWeek] = useState(1);
  const [pickerDay, setPickerDay] = useState(1);

  useEffect(() => {
    if (!isOpen || !data) return;
    const sd = data.selectedDate ? new Date(data.selectedDate) : new Date();
    setOverlaySelectedDate(sd);
    setOverlayCurrentView(data.currentView || 'timeGridWeek');
    setPickerMonth(sd.getMonth());
    setPickerYear(sd.getFullYear());
    setPickerWeek(getWeekNumber(sd));
    setPickerDay(sd.getDate());
  }, [isOpen, data?.selectedDate, data?.currentView]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isPickerOpen && overlayPickerRef.current && !overlayPickerRef.current.contains(e.target)) {
        setIsPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPickerOpen]);

  const handleOverlayTitleClick = (date, viewType) => {
    setOverlayCurrentView(viewType);
    const currentDate = new Date(date);
    setPickerYear(currentDate.getFullYear());
    setPickerMonth(currentDate.getMonth());
    setPickerWeek(getWeekNumber(currentDate));
    setPickerDay(currentDate.getDate());
    setIsPickerOpen(true);
  };

  const handleOverlayPickerSelect = () => {
    const monthNum = Number(pickerMonth); // 0=Jan, 1=Feb, ... 11=Dec
    const yearNum = Number(pickerYear);
    const dayNum = Number(pickerDay);
    let newDate;
    let dateStr; // YYYY-MM-DD for FullCalendar (no timezone/Date conversion)
    if (overlayCurrentView === 'dayGridMonth') {
      newDate = new Date(yearNum, monthNum, 1);
      dateStr = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-01`;
    } else if (overlayCurrentView === 'timeGridWeek') {
      newDate = getDateFromWeek(Number(pickerWeek), yearNum);
      dateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
    } else if (overlayCurrentView === 'timeGridDay') {
      newDate = new Date(yearNum, monthNum, dayNum);
      dateStr = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
    } else {
      newDate = new Date(yearNum, monthNum, 1);
      dateStr = `${yearNum}-${String(monthNum + 1).padStart(2, '0')}-01`;
    }
    // Navigate the calendar first using a date string (avoids UTC/Date conversion bugs in full-screen)
    if (overlayCalendarRef.current) {
      const api = overlayCalendarRef.current.getApi();
      if (api) {
        if (overlayCurrentView === 'dayGridMonth') {
          api.changeView('dayGridMonth', dateStr);
        } else if (overlayCurrentView === 'timeGridWeek') {
          api.changeView('timeGridWeek', dateStr);
        } else if (overlayCurrentView === 'timeGridDay') {
          api.changeView('timeGridDay', dateStr);
        } else {
          api.gotoDate(dateStr);
        }
      }
    }
    setOverlaySelectedDate(newDate);
    setIsPickerOpen(false);
  };

  const handleOverlayDatesSet = (dateInfo) => {
    if (dateInfo?.start) {
      setOverlaySelectedDate(new Date(dateInfo.start));
      if (overlayCalendarRef.current) {
        const api = overlayCalendarRef.current.getApi();
        if (api?.view) setOverlayCurrentView(api.view.type);
      }
    }
    data.onDatesSet?.(dateInfo);
  };

  if (!isOpen) return null;

  const Calendar = require('../components/Schedule/calendar').default;
  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={modalVariants}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onClose();
            }
          }}
        >
          <motion.div
            className="bg-white rounded-xl shadow-2xl w-full h-full flex flex-col"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100vw',
              height: '100vh',
              maxWidth: '100vw',
              maxHeight: '100vh'
            }}
          >
            {/* Overlay Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
              <h2 className="text-xl font-semibold text-gray-900">Amenity Booking Calendar</h2>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            {/* Calendar in Overlay - Full Screen (with same date picker as inline) */}
            <div className="flex-1 overflow-auto relative p-4 min-h-0">
              <Calendar
                ref={overlayCalendarRef}
                selectedDate={overlaySelectedDate}
                events={data.events || []}
                eventContent={data.eventContent || undefined}
                slotMinTime="06:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                nowIndicator={true}
                height="100%"
                initialView={overlayCurrentView}
                stableKey
                onEventClick={data.onEventClick || undefined}
                onTitleClick={handleOverlayTitleClick}
                onDatesSet={handleOverlayDatesSet}
              />
              {isPickerOpen && (
                <div
                  ref={overlayPickerRef}
                  className="absolute top-16 left-1/2 transform -translate-x-1/2 z-50 bg-white rounded-lg shadow-xl border border-gray-200 p-4 min-w-[280px] max-w-[400px]"
                >
                  <div className="mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      {overlayCurrentView === 'dayGridMonth' && 'Select Month & Year'}
                      {overlayCurrentView === 'timeGridWeek' && 'Select Week & Year'}
                      {overlayCurrentView === 'timeGridDay' && 'Select Day, Month & Year'}
                    </h3>
                    {overlayCurrentView === 'dayGridMonth' && (
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                          <select
                            value={pickerMonth}
                            onChange={(e) => setPickerMonth(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                          >
                            {Array.from({ length: 12 }, (_, i) => (
                              <option key={i} value={i}>{new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}</option>
                            ))}
                          </select>
                        </div>
                        <div className="flex-1">
                          <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                          <select
                            value={pickerYear}
                            onChange={(e) => setPickerYear(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                          >
                            {Array.from({ length: 20 }, (_, i) => {
                              const y = new Date().getFullYear() - 10 + i;
                              return <option key={y} value={y}>{y}</option>;
                            })}
                          </select>
                        </div>
                      </div>
                    )}
                    {overlayCurrentView === 'timeGridWeek' && (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                          <select
                            value={pickerYear}
                            onChange={(e) => { setPickerYear(parseInt(e.target.value)); setPickerWeek(1); }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                          >
                            {Array.from({ length: 20 }, (_, i) => {
                              const y = new Date().getFullYear() - 10 + i;
                              return <option key={y} value={y}>{y}</option>;
                            })}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Week</label>
                          <select
                            value={pickerWeek}
                            onChange={(e) => setPickerWeek(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white max-h-48 overflow-y-auto"
                          >
                            {getWeeksForYear(pickerYear).map((week) => (
                              <option key={week.number} value={week.number}>{week.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    {overlayCurrentView === 'timeGridDay' && (
                      <div className="space-y-3">
                        <div className="flex gap-3">
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Month</label>
                            <select
                              value={pickerMonth}
                              onChange={(e) => {
                                const m = parseInt(e.target.value);
                                setPickerMonth(m);
                                const daysInMonth = new Date(pickerYear, m + 1, 0).getDate();
                                if (pickerDay > daysInMonth) setPickerDay(daysInMonth);
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                            >
                              {Array.from({ length: 12 }, (_, i) => (
                                <option key={i} value={i}>{new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex-1">
                            <label className="block text-xs font-medium text-gray-600 mb-1">Year</label>
                            <select
                              value={pickerYear}
                              onChange={(e) => setPickerYear(parseInt(e.target.value))}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                            >
                              {Array.from({ length: 20 }, (_, i) => {
                                const y = new Date().getFullYear() - 10 + i;
                                return <option key={y} value={y}>{y}</option>;
                              })}
                            </select>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Day</label>
                          <select
                            value={pickerDay}
                            onChange={(e) => setPickerDay(parseInt(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                          >
                            {getDaysForMonth(pickerMonth, pickerYear).map((dayItem) => (
                              <option key={dayItem.day} value={dayItem.day}>{dayItem.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 justify-end">
                    <button
                      type="button"
                      onClick={() => setIsPickerOpen(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleOverlayPickerSelect}
                      className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      Go
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const LiveOrderDetailsOverlayWrapper = ({ onClose, order, onAccept, onDecline }) => {
  if (!order) return null;
  const LiveOrderDetailsOverlay = require('../components/LiveOrderFeed/LiveOrderDetailsOverlay').default;
  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-[4px] p-4 overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        className="w-full max-w-3xl max-h-[90vh] m-auto"
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.96, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Card.NoShadowGradient className="w-full max-h-[90vh] overflow-y-auto rounded-2xl">
          <LiveOrderDetailsOverlay onClose={onClose} order={order} onAccept={onAccept} onDecline={onDecline} />
        </Card.NoShadowGradient>
      </motion.div>
    </motion.div>
  );
};

const InventoryDatePickerOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen) return null;
  
  // Import DatePicker component dynamically to avoid circular dependencies
  const DatePicker = require('../components/ShiftManagement/datepicker').default;
  
  return (
    <DatePicker
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={(result) => {
        if (data.onConfirm && typeof data.onConfirm === 'function') {
          data.onConfirm(result);
        }
        onClose();
      }}
      initialMode={data.mode || 'range'}
      initialStartDate={data.startDate || new Date()}
      initialEndDate={data.endDate || new Date(Date.now() + 6 * 24 * 60 * 60 * 1000)}
      initialSingleDate={data.singleDate || new Date()}
      title="Select Date Range"
    />
  );
};

const ProductionDetailsOverlay = ({ isOpen, onClose, data }) => {
  if (!isOpen || !data.productionRun) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const handleDownload = () => {
    if (data.onDownload && typeof data.onDownload === 'function') {
      data.onDownload(data.productionRun);
    }
  };

  const run = data.productionRun;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-800">
                {run.production_name ||
                  run.template_name ||
                  run.name ||
                  'Unknown Template'}{' '}
                - Details
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Name
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.production_name ||
                      run.template_name ||
                      run.name ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Type
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.production_name ||
                      run.template_name ||
                      run.type ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Quantity
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.units_produced ||
                      run.quantity ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Raw Material Cost
                  </label>
                  <p className="text-lg font-medium text-green-600 mt-1">
                    ₹
                    {(
                      run.raw_material_cost ||
                      run.rawMaterialCost ||
                      0
                    ).toFixed(2)}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Batch ID
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.batch_reference ||
                      run.batch_id ||
                      run.batchId ||
                      'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Date
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.production_date || run.date
                      ? new Date(
                          run.production_date || run.date
                        ).toLocaleDateString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">
                    Site
                  </label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {run.site_name ||
                      run.siteName ||
                      'N/A'}
                  </p>
                </div>
                {run.notes && (
                  <div className="col-span-2">
                    <label className="text-sm font-semibold text-gray-600">
                      Notes
                    </label>
                    <p className="text-lg font-medium text-gray-800 mt-1">
                      {run.notes}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleDownload}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <Download
                    className="w-5 h-5 text-white stroke-white"
                    strokeWidth={2}
                  />
                  <span style={{ color: '#ffffff' }}>Download Details</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-gray-700 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <span style={{ color: '#ffffff' }}>Close</span>
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const VendorDetailsOverlay = ({ isOpen, onClose, data }) => {
  const [sitesMap, setSitesMap] = useState({});
  const [isLoadingSites, setIsLoadingSites] = useState(false);

  // Fetch all sites when overlay opens
  useEffect(() => {
    if (!isOpen || !data.vendor) return;

    const fetchSites = async () => {
      setIsLoadingSites(true);
      try {
        const sitesResponse = await getAllSites();
        
        // Handle different response formats
        let sitesArray = [];
        if (Array.isArray(sitesResponse)) {
          sitesArray = sitesResponse;
        } else if (sitesResponse.sites && Array.isArray(sitesResponse.sites)) {
          sitesArray = sitesResponse.sites;
        } else if (sitesResponse.data && Array.isArray(sitesResponse.data)) {
          sitesArray = sitesResponse.data;
        }

        // Create a map of site_id to site_name
        const map = {};
        sitesArray.forEach(site => {
          const siteId = site.site_id || site.id;
          const siteName = site.site_name || site.name || siteId;
          if (siteId) {
            map[siteId] = siteName;
          }
        });
        setSitesMap(map);
      } catch (error) {
        console.error('Error fetching sites:', error);
        setSitesMap({});
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
  }, [isOpen, data.vendor]);

  if (!isOpen || !data.vendor) return null;

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const handleDownload = () => {
    if (data.onDownload && typeof data.onDownload === 'function') {
      data.onDownload(data.vendor);
    }
  };

  const vendor = data.vendor;

  // Helper function to get site name from site ID or object
  const getSiteName = (site) => {
    if (typeof site === 'string') {
      // If it's a string (site ID), look it up in the map
      return sitesMap[site] || site;
    } else if (site && typeof site === 'object') {
      // If it's an object, try to get site_name or name
      return site.site_name || site.name || site.site_id || site.id || 'Unknown Site';
    }
    return 'Unknown Site';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold text-gray-800">
                {vendor.vendorName} - Details
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-semibold text-gray-600">Vendor Name</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.vendorName}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Email</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.email || 'N/A'}
                  </p>
                </div>
                <div className="col-span-2">
                  <label className="text-sm font-semibold text-gray-600">Address</label>
                  <p className="text-base text-gray-800 mt-1">
                    {vendor.address 
                      ? `${vendor.address}, ${vendor.city}, ${vendor.state} - ${vendor.pinCode}, ${vendor.country}`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">PAN Number</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.panNumber || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">GST Number</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.gstNumber || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Bank Name</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.bankName || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contact Person</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.contactPerson || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contact Phone</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.contactPhone || 'N/A'}
                  </p>
                </div>
                {vendor.secondaryContact && (
                  <>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Secondary Contact Name</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {vendor.secondaryContact.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Secondary Contact Role</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {vendor.secondaryContact.role || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Secondary Contact Mobile</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {vendor.secondaryContact.mobile || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-600">Secondary Contact Email</label>
                      <p className="text-lg font-medium text-gray-800 mt-1">
                        {vendor.secondaryContact.email || 'N/A'}
                      </p>
                    </div>
                  </>
                )}
                {vendor.leadTimeDays !== undefined && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Lead Time (Days)</label>
                    <p className="text-lg font-medium text-gray-800 mt-1">
                      {vendor.leadTimeDays} days
                    </p>
                  </div>
                )}
                {vendor.paymentTerms && (
                  <div>
                    <label className="text-sm font-semibold text-gray-600">Payment Terms</label>
                    <p className="text-lg font-medium text-gray-800 mt-1">
                      {vendor.paymentTerms}
                    </p>
                  </div>
                )}
                <div>
                  <label className="text-sm font-semibold text-gray-600">Account Number</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.accountNo || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">IFSC Code</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.ifscNo || 'N/A'}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Status</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    <span className={`px-2 py-1 rounded text-sm ${
                      vendor.status === 'active' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {vendor.status || 'active'}
                    </span>
                  </p>
                </div>
                <div>
                  <label className="text-sm font-semibold text-gray-600">Created Date</label>
                  <p className="text-lg font-medium text-gray-800 mt-1">
                    {vendor.createdAt 
                      ? new Date(vendor.createdAt).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Associated Sites</h3>
                <div className="space-y-2">
                  {isLoadingSites ? (
                    <p className="text-sm text-gray-500">Loading sites...</p>
                  ) : (() => {
                    // Use site_ids if available (from API), otherwise fall back to sites array
                    const siteIds = Array.isArray(vendor.site_ids) && vendor.site_ids.length > 0 
                      ? vendor.site_ids 
                      : (Array.isArray(vendor.sites) ? vendor.sites : []);
                    
                    if (siteIds.length === 0) {
                      return <p className="text-sm text-gray-500">No sites associated</p>;
                    }
                    
                    return siteIds.map((siteId, index) => {
                      const siteName = getSiteName(siteId);
                      return (
                        <div key={index} className="px-4 py-2 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium text-gray-800">
                            {siteName}
                          </p>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">Items Procured</h3>
                <div className="space-y-2">
                  {Array.isArray(vendor.itemsProcure) && vendor.itemsProcure.length > 0 ? (
                    vendor.itemsProcure.map((item, index) => (
                      <div key={index} className="px-4 py-2 bg-gray-50 rounded-lg">
                        <p className="text-sm font-medium text-gray-800">{item}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-gray-500">No items listed</p>
                  )}
                </div>
              </div>

              {/* <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={handleDownload}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-700 px-6 py-3 rounded-xl font-semibold hover:bg-blue-800 transition-colors"
                  style={{ color: '#ffffff' }}
                >
                  <Download className="w-5 h-5 text-white stroke-white" strokeWidth={2} />
                  <span style={{ color: '#ffffff' }}>Download Details</span>
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 bg-gray-200 text-gray-800 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div> */}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
